import { Link } from 'react-router-dom';
import { Receipt } from 'lucide-react';

interface Transaction {
  id: number;
  date: string;
  payee: string;
  amount: number;
  type: 'expense' | 'income';
  envelope_name?: string;
}

interface RecentTransactionsProps {
  transactions: Transaction[];
  isLoading?: boolean;
}

export default function RecentTransactions({ transactions, isLoading }: RecentTransactionsProps) {
  if (isLoading) {
    return (
      <div className="card p-0 overflow-hidden">
        <div className="p-6 border-b border-border">
          <h3 className="text-lg font-semibold text-text-header">Recent Transactions</h3>
          <p className="text-sm text-text-muted">Latest activity</p>
        </div>
        <div className="divide-y divide-border">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="p-6 animate-pulse">
              <div className="flex items-center justify-between">
                <div className="h-4 bg-background-subtle rounded w-1/2" />
                <div className="h-4 bg-background-subtle rounded w-20" />
              </div>
              <div className="mt-3 h-3 bg-background-subtle rounded w-1/3" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (transactions.length === 0) {
    return (
      <div className="card">
        <div className="flex items-center justify-between pb-5 border-b border-border">
          <div>
            <h3 className="text-lg font-semibold text-text-header">Recent Transactions</h3>
            <p className="text-sm text-text-muted">Latest activity</p>
          </div>
        </div>
        <div className="pt-10 pb-8 text-center">
          <div className="mx-auto h-12 w-12 bg-primary/10 rounded-full flex items-center justify-center mb-4 border border-primary/20">
            <Receipt className="h-6 w-6 text-primary" />
          </div>
          <p className="text-sm text-text-muted mb-5">No transactions yet.</p>
          <Link to="/transactions" className="btn btn-primary inline-flex">
            Add a transaction
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="card p-0 overflow-hidden">
      <div className="p-6 border-b border-border flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-text-header">Recent Transactions</h3>
          <p className="text-sm text-text-muted">Latest activity</p>
        </div>
        <Link to="/transactions" className="text-sm font-medium text-primary hover:text-primary-hover transition-colors">
          View all
        </Link>
      </div>

      <div className="divide-y divide-border">
        {transactions.map((transaction) => {
          const amountColor = transaction.type === 'income' ? 'text-success' : 'text-text-header';
          const sign = transaction.type === 'income' ? '+' : '-';

          return (
            <div key={transaction.id} className="p-6 hover:bg-background-subtle/40 transition-colors duration-200">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-text-header truncate">{transaction.payee}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-xs text-text-muted">{formatDate(transaction.date)}</span>
                    {transaction.envelope_name && (
                      <span className="text-xs px-2 py-0.5 rounded-full border border-border bg-background-subtle text-text-body max-w-[12rem] truncate">
                        {transaction.envelope_name}
                      </span>
                    )}
                  </div>
                </div>
                <span className={`text-sm font-semibold ${amountColor}`}>
                  {sign}{formatCurrency(Math.abs(transaction.amount))}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}
